import { Children } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { cardClasses } from './Card';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * "Nothing to do, and that is fine": an operational screen that is ready and waiting, as opposed to
 * `EmptyState` ("nothing here"). One white work surface with one heading; `id` names the section so it
 * is a region a screen reader can jump to. `children` is for a real figure or a status badge the screen
 * already has, never a second action: the screen's Refresh stays in its header.
 */
export function ReadyState({
  id,
  title,
  message,
  icon = 'check',
  level = 2,
  children,
  className,
}: {
  id: string;
  title: string;
  message?: string;
  icon?: IconName;
  /** 2 under a page title; 3 inside a titled section such as the counter's "Sessions". */
  level?: 2 | 3;
  children?: ReactNode;
  className?: string;
}) {
  const Heading = level === 3 ? 'h3' : 'h2';
  const extras = Children.toArray(children);
  return (
    <section
      aria-labelledby={id}
      className={cn(
        cardClasses({ padding: 'none' }),
        'flex flex-col items-center gap-3 px-5 py-10 text-center md:py-14',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="grid size-12 place-items-center rounded-full bg-success-soft text-success"
      >
        <Icon name={icon} size={24} />
      </span>
      <div className="flex flex-col gap-1">
        <Heading id={id} className="text-xl leading-7 font-semibold text-ink">
          {title}
        </Heading>
        {message && <p className="mx-auto max-w-sm text-sm text-muted">{message}</p>}
      </div>
      {extras.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {extras}
        </div>
      )}
    </section>
  );
}
